import {
  centsToDollars,
  totalSellerPriceCentsForEnrollments,
} from "@outschool/business-rules";
import {
  ActivityEnrollQueryQuery,
  ActivityEnrollQueryQueryVariables,
  ActivitySelfPacedEnrollQueryQuery,
  ActivitySelfPacedEnrollQueryQueryVariables,
  ClassDetailsNoSectionsQuery,
  CreateEnrollmentsMutation,
  SettleOrderMutation,
} from "@outschool/gql-frontend-generated";
import { useCurrencyLocalization } from "@outschool/localization";
import {
  createPathWithActivity,
  enrolledPath,
  websiteHomePath,
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import {
  IntegrationCategory,
  useOrderCompleted,
  useTrackEvent,
} from "@outschool/ui-analytics";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { currentUserQuery, useSession } from "@outschool/ui-auth";
import { LoadingModal, Modal } from "@outschool/ui-legacy-component-library";
import { useBooleanState, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useHistory, useLocation } from "react-router";

import * as Routes from "../../shared/Routes";
import * as User from "../../shared/User";
import ActivityEnrollWithStripeElements, {
  Step as EnrollmentStep,
} from "../components/enrolling/ActivityEnrollWithStripeElements";
import GiftInvitationsModal from "../components/enrolling/GiftInvitationsModal";
import { EsaMembershipSavePaymentMethod } from "../components/esaMemberships/EsaMembershipSavePaymentMethod";
import { useMembershipRequirement } from "../components/subscriptions/hooks/useMembershipRequirement";
import { usePaymentMethods } from "../hooks/CurrentUser";
import {
  ActivityEnrollQuery,
  ActivitySelfPacedEnrollQuery,
} from "../queries/ActivityEnrollQuery";
import {
  useTrackNewBuyerAcquiredMutation,
  useTrackOrderCompletedMutation,
} from "../queries/TrackingMutation";
import { useAppState } from "../stores/AppStateProvider";

// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

export type GiftEnrollmentState = {
  giftEnrollmentCount: number;
  acceptGiftSlugId: string;
};

type ActivityEnrollPageProps = {
  activity: ClassDetailsNoSectionsQuery["activity"];
};

type ConfirmedEnrollments =
  | SettleOrderMutation["settleOrder"]["enrollments"]
  | CreateEnrollmentsMutation["createEnrollments"]["enrollments"];

type ConfirmedGiftEnrollments =
  | SettleOrderMutation["settleOrder"]["giftEnrollments"]
  | CreateEnrollmentsMutation["createEnrollments"]["giftEnrollments"];

function isUserPaidEnrollment(
  enrollment:
    | SettleOrderMutation["settleOrder"]["enrollments"][number]
    | CreateEnrollmentsMutation["createEnrollments"]["enrollments"][number],
): enrollment is SettleOrderMutation["settleOrder"]["enrollments"][number] {
  return enrollment.hasOwnProperty("isFirstPurchaseByUser");
}

export default function ActivityEnrollPage({
  activity: propsActivity,
}: ActivityEnrollPageProps) {
  const location = useLocation<{ returnLocation?: string }>();
  const history = useHistory();
  const navigate = useNavigation();
  const appState = useAppState();
  const trackeServerSideOrderCompleted = useTrackOrderCompletedMutation();
  const trackOrderCompleted = useOrderCompleted();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const { currencyCode } = useCurrencyLocalization();
  const trackNewBuyerAcquiredCapi = useTrackNewBuyerAcquiredMutation();

  const [paymentComplete, setPaymentComplete] = useBooleanState(false);
  const [giftEnrollmentState, setGiftEnrollmentState] =
    React.useState<GiftEnrollmentState | null>(null);
  const [confirmedEnrollments, setConfirmedEnrollments] =
    React.useState<ConfirmedEnrollments>();
  const [esaShowSavePayment, setEsaShowSavePayment] = React.useState(false);
  const [esaSavePaymentComplete, setEsaSavePaymentComplete] =
    React.useState(false);
  // TODO: Remove this after switching to OsModal, this is a shim to prevent scroll lock
  const [isEnrollmentComplete, setIsEnrollmentComplete] = React.useState(false);
  const firstConfirmedEnrollmentUid = confirmedEnrollments?.[0]?.uid;
  const [enrollmentStep, setEnrollmentStep] = React.useState<
    EnrollmentStep | undefined
  >();
  const { mustPurchaseMembership } = useMembershipRequirement();

  // TODO: Remove this after switching to OsModal, this is a shim to prevent scroll lock
  // Handle redirect after enrollment completion to prevent scroll lock
  React.useEffect(() => {
    if (isEnrollmentComplete && confirmedEnrollments) {
      history.replace(websiteHomePath(), {
        confirmedEnrollments,
        activity: propsActivity,
        scrollToTop: true,
      });
    }
    return undefined;
  }, [isEnrollmentComplete, confirmedEnrollments, history, propsActivity]);

  const { currentUser: enrollingUser } = useSession();
  const { paymentMethods } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  const isRedirecting = React.useRef(false);
  const hasEsaMembership = enrollingUser?.hasEsaMembership;

  // If someone gets ahold of a direct link to the enroll page, kick them back to the join membership modal if required
  React.useEffect(() => {
    if (
      mustPurchaseMembership &&
      !hasEsaMembership &&
      location.pathname.includes("/enroll") &&
      !isRedirecting.current
    ) {
      isRedirecting.current = true;
      // pull out /enroll part so we can avoid the double-modal situation
      const pathWithoutEnroll = location.pathname.replace(/\/enroll$/, "");
      const queryParams = new URLSearchParams();
      // eslint-disable-next-line i18next/no-literal-string
      queryParams.set("joinMembershipModal", "true");
      queryParams.set(
        // eslint-disable-next-line i18next/no-literal-string
        "redirectAfterPath",
        `${location.pathname}?${location.search}`,
      );
      navigate(`${pathWithoutEnroll}?${queryParams.toString()}`, {
        replace: true,
      });
    }
  }, [
    mustPurchaseMembership,
    hasEsaMembership,
    location.pathname,
    location.search,
    navigate,
  ]);

  const isSelfPaced = propsActivity.isSelfPaced;

  const sectionUid = location.query.sectionUid;
  const skip = !isSelfPaced && !sectionUid;

  React.useEffect(() => {
    // Redirect to activity page if for some reason the sectionUid parameter is missing
    if (skip && propsActivity) {
      history.push(createPathWithActivity(propsActivity));
    }

    // This effect was originally in a componentDidMount lifecycle method. Use
    // empty deps to maintain behavior, so this check is only made on the
    // initial render
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (paymentComplete && esaSavePaymentComplete) {
      history.replace(websiteHomePath(), {
        confirmedEnrollments,
        activity: propsActivity,
        scrollToTop: true,
      });
    }
  }, [
    paymentComplete,
    history,
    confirmedEnrollments,
    propsActivity,
    esaSavePaymentComplete,
  ]);

  const startAfter = React.useMemo(() => dayjs().toDate(), []);

  const {
    data: sectionEnrollData,
    loading: sectionEnrollLoading,
    refetch: sectionEnrollRefetch,
    error: sectionEnrollError,
  } = useQueryWithPreviousData<
    ActivityEnrollQueryQuery,
    ActivityEnrollQueryQueryVariables
  >(ActivityEnrollQuery, {
    fetchPolicy: "network-only",
    skip: !sectionUid,
    variables: {
      sectionUid,
      startAfter,
    },
  });

  const {
    data: selfPacedEnrollData,
    loading: selfPacedEnrollLoading,
    refetch: selfPacedEnrollRefetch,
    error: selfPacedEnrollError,
  } = useQueryWithPreviousData<
    ActivitySelfPacedEnrollQueryQuery,
    ActivitySelfPacedEnrollQueryQueryVariables
  >(ActivitySelfPacedEnrollQuery, {
    fetchPolicy: "network-only",
    skip: !isSelfPaced,
    variables: {
      activityUid: propsActivity.uid,
    },
  });

  const data = isSelfPaced ? selfPacedEnrollData : sectionEnrollData;
  const loading = isSelfPaced ? selfPacedEnrollLoading : sectionEnrollLoading;
  const refetch = isSelfPaced ? selfPacedEnrollRefetch : sectionEnrollRefetch;
  const error = isSelfPaced ? selfPacedEnrollError : sectionEnrollError;

  if (skip || error) {
    return null;
  }
  if (loading || !data) {
    return (
      <Modal open closeViaMaskClick={false} closeViaEscButton={false}>
        <LoadingModal />
      </Modal>
    );
  }

  // @ts-ignore TS(2339): Property 'enrollmentForm' does not exist on type '... Remove this comment to see the full error message
  const { enrollmentForm, currentUser } = isSelfPaced
    ? {
        ...data,
        enrollmentForm: (data as ActivitySelfPacedEnrollQueryQuery)
          .selfPacedEnrollmentForm,
      }
    : data;
  const {
    activity,
    section,
    currentUserEnrollments,
    conflictingEnrollments = [],
    pendingEnrollments,
    waitlistSeatsCount,
  } = enrollmentForm ?? {};

  const userTimeZone = User.timeZone(currentUser);

  const completePaymentFlow = async ({
    enrollments,
    giftEnrollments,
    isRecurringEnrollment,
  }: {
    enrollments: ConfirmedEnrollments;
    giftEnrollments: ConfirmedGiftEnrollments;
    isRecurringEnrollment?: boolean;
  }) => {
    const shouldShowSavePaymentForm =
      !!currentUser?.hasEsaMembership &&
      !defaultPaymentMethod &&
      !!isRecurringEnrollment;

    setEsaShowSavePayment(shouldShowSavePaymentForm);
    setConfirmedEnrollments(enrollments);
    setPaymentComplete();

    // Set esaSavePaymentComplete to true for non-ESA payment completions
    // to ensure the modal closes and scroll lock is released immediately
    // ESA users will have this set in handleEsaSavedPaymentClose()
    // TODO: Remove this after switching to OsModal, this is a shim to prevent scroll lock
    if (!shouldShowSavePaymentForm) {
      setEsaSavePaymentComplete(true);
    }

    // @ts-ignore TS(2532): Object is possibly 'undefined'.
    if (giftEnrollments?.length > 0) {
      setGiftEnrollmentState({
        // @ts-ignore TS(2531): Object is possibly 'null'.
        giftEnrollmentCount: giftEnrollments.length,
        // @ts-ignore TS(2531): Object is possibly 'null'.
        acceptGiftSlugId: giftEnrollments[0].acceptGiftSlugId,
      });
    }

    // Mark enrollment as complete for users with subscription credits
    // This will trigger the redirect via useEffect
    if (
      currentUser?.canPayWithSubscriptionCredits &&
      (!currentUser?.hasEsaMembership ||
        !!defaultPaymentMethod ||
        !isRecurringEnrollment)
    ) {
      setIsEnrollmentComplete(true);
    }
  };

  const handlePaymentComplete = async ({
    enrollments,
    giftEnrollments,
    orderSlugId,
  }: {
    enrollments: SettleOrderMutation["settleOrder"]["enrollments"];
    giftEnrollments: SettleOrderMutation["settleOrder"]["giftEnrollments"];
    orderSlugId: string;
  }) => {
    // TODO: These enforcements are because of some weird behavior in
    //       ActivityEnrollQuery and currentUserQuery, probably some race condition as both queries ask for
    //       current user. Once Apollo is upgraded, try to cleanup these logics.
    if (data) {
      try {
        refetch();
      } catch (err) {
        // longstanding bug with refetch blowing up, see https://sentry.io/organizations/outschool/issues/1530195572
        // explicit log to gather more info on state
        OsPlatform.captureError(err, { extra: { propsData: data } });
      }
    }
    appState.apolloClient.query({
      query: currentUserQuery,
      fetchPolicy: "network-only",
    });
    const totalCents = totalSellerPriceCentsForEnrollments([
      ...enrollments,
      // @ts-ignore TS(2488): Type '{ __typename?: "GiftEnrollment" | undefined;... Remove this comment to see the full error message
      ...giftEnrollments,
    ]);
    const dollarAmount = centsToDollars(totalCents);

    const { activity } = enrollmentForm;
    const isFirstPurchase = enrollments.some(
      e => isUserPaidEnrollment(e) && e.isFirstPurchaseByUser,
    );

    if (totalCents) {
      if (!!activity.is_ongoing_weekly) {
        trackAdEvent("Subscription Started", {
          activitySlugId: activity.slug_id,
          orderId: orderSlugId,
          revenue: dollarAmount,
          total: dollarAmount,
          value: dollarAmount,
          currency: currencyCode,
        });
      }

      trackeServerSideOrderCompleted({
        enrollmentConfirmedAt:
          enrollments.length > 0
            ? enrollments[0].confirmedAt
            : // @ts-ignore TS(2531): Object is possibly 'null'.
              giftEnrollments[0].confirmedAt,
        name: activity.title,
        orderId: orderSlugId,
        price: activity.price_cents,
        productId: activity.slug_id,
        quantity: enrollments.length,
        total: totalCents,
      });

      trackOrderCompleted({
        activityListing: {
          activity,
          leader: section?.leader || activity.leader,
        },
        enrollments: [...enrollments, ...giftEnrollments],
        section,
        // @ts-ignore
        user: currentUser,
      });
    }

    if (isFirstPurchase) {
      trackAdEvent("New Buyer Acquired", {
        revenue: dollarAmount,
        total: dollarAmount,
        currency: currencyCode,
      });
      trackNewBuyerAcquiredCapi({
        amount: totalCents,
      });
    }
  };

  const handleEsaSavedPaymentClose = () => {
    setEsaShowSavePayment(false);
    setEsaSavePaymentComplete(true);
  };

  const handleClose = () => {
    if (esaShowSavePayment) {
      handleEsaSavedPaymentClose();
    } else if (paymentComplete) {
      history.replace(enrolledPath(sectionUid));
    } else if (location.state && location.state.returnLocation) {
      history.replace(location.state.returnLocation);
    } else {
      const { activity } = enrollmentForm;
      history.replace(Routes.activityPath(activity));
    }
  };

  let currentView: React.ReactNode;
  if (loading && !activity) {
    currentView = <LoadingModal />;
  } else if (paymentComplete && !esaShowSavePayment) {
    if (esaSavePaymentComplete) {
      currentView = null;
    }
    // @ts-ignore TS(2532): Object is possibly 'undefined'.
    else if (giftEnrollmentState?.giftEnrollmentCount > 0) {
      currentView = (
        // @ts-ignore TS(2322): Type '{ giftEnrollmentCount?: number | undefined; ... Remove this comment to see the full error message
        <GiftInvitationsModal
          sectionUid={sectionUid}
          {...giftEnrollmentState}
        />
      );
    } else if (activity.isClub) {
      navigate(
        Routes.websiteRoutes.redirectToLearnerUrl({
          learnerAppUrl: Routes.learnerAppClubUrl(sectionUid),
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          enrollmentUid: firstConfirmedEnrollmentUid,
        }),
        { hardNav: true, replace: true },
      );
    } else {
      if (isSelfPaced) {
        const newSectionUid =
          (confirmedEnrollments && confirmedEnrollments[0]?.section_uid) ||
          sectionUid;
        history.replace(
          Routes.websiteRoutes.enrollmentConfirmationPath(newSectionUid),
        );
      } else {
        history.replace(
          Routes.websiteRoutes.enrollmentConfirmationPath(sectionUid),
        );
      }
    }
  } else if (esaShowSavePayment) {
    currentView = (
      <EsaMembershipSavePaymentMethod onClose={handleEsaSavedPaymentClose} />
    );
  } else {
    currentView = (
      <ActivityEnrollWithStripeElements
        activity={activity}
        section={section}
        enrollingParentEnrollments={currentUserEnrollments}
        pendingEnrollments={pendingEnrollments}
        waitlistSeatsCount={waitlistSeatsCount}
        userTimeZone={userTimeZone}
        // @ts-ignore
        payingUser={currentUser}
        enrollingParent={currentUser}
        conflictingEnrollments={conflictingEnrollments}
        onPaymentComplete={handlePaymentComplete}
        onClose={handleClose}
        onEnrollmentStepChange={setEnrollmentStep}
        onCompleteFlow={completePaymentFlow}
      />
    );
  }

  /*
   * We only allow the parent to close the Modal via the top button
   * in "paymentComplete" mode. Otherwise, it is possible to close
   * the payment modal while a request is in flight, breaking our some flows.
   */
  return (
    <Modal
      open={!esaSavePaymentComplete}
      onClose={handleClose}
      closeViaMaskClick={
        enrollmentStep === EnrollmentStep.ChooseSelfPacedStartTime
      }
      closeViaEscButton={
        enrollmentStep === EnrollmentStep.ChooseSelfPacedStartTime
      }
      hasCloseButton={
        paymentComplete ||
        enrollmentStep === EnrollmentStep.ChooseSelfPacedStartTime ||
        enrollmentStep === EnrollmentStep.ChooseOngoingChooseStartTime
      }
      sx={{
        padding: "1.5em",
        maxWidth: "640px",
        width:
          enrollmentStep === EnrollmentStep.ChooseOngoingChooseStartTime
            ? "fit-content"
            : "100%",
      }}
    >
      {currentView}
    </Modal>
  );
}
